<template>
  <b-section-loader :loading="$loading.isLoading(['taskTypes'])">
    <div class="d-flex flex-column ga-6">
      <div>
        <div class="d-flex justify-space-between align-center mb-2">
          <div class="d-flex align-center ga-2">
            <v-icon icon="mdi-format-title" size="x-small" />
            <span class="label mb-0">Назва</span>
          </div>
          <span class="text-caption text-disabled">
            {{ topicLength }}/128
          </span>
        </div>
        <b-input
          v-model="task.topic"
          :error-messages="getErrorMessages(v$.topic)"
          :readonly="task.status && task.status.id !== 1"
          counter="128"
          hide-details
          maxlength="128"
          placeholder="Наприклад: Підготувати квартальний звіт"
          @blur="v$.topic.$touch()" />
      </div>

      <div>
        <div class="d-flex justify-space-between align-center mb-2">
          <div class="d-flex align-center ga-2">
            <v-icon icon="mdi-format-align-left" size="x-small" />
            <span class="label mb-0">Опис</span>
          </div>
          <span class="text-caption text-disabled">
            {{ descriptionLength }} символів
          </span>
        </div>
        <b-textarea
          v-model="task.description"
          :error-messages="getErrorMessages(v$.description)"
          :readonly="disabled"
          hide-details
          placeholder="Опишіть деталі: що потрібно зробити, які матеріали використати, який очікуваний результат..."
          rows="4"
          @blur="v$.description.$touch()"></b-textarea>
      </div>

      <div v-if="!taskType.familiarization">
        <div class="d-flex align-center ga-2 mb-2">
          <v-icon icon="mdi-account-outline" size="x-small" />
          <span class="label mb-0">Виконавець</span>
        </div>
        <employee-autocomplete
          v-model="task.executorId"
          :default-item="task.executor"
          :error-messages="getErrorMessages(v$.executorId)"
          @blur="v$.executorId.$touch()"></employee-autocomplete>
      </div>

      <div>
        <div class="d-flex align-center ga-2 mb-2">
          <v-icon icon="mdi-calendar-blank-outline" size="x-small" />
          <span class="label mb-0">Термін виконання</span>
        </div>
        <div class="d-flex flex-wrap ga-1 mb-3">
          <v-btn
            v-for="preset in deadlinePresets"
            :key="preset.value"
            :active="task.deadline === preset.value"
            border
            color="white"
            size="small"
            @click="setDeadline(preset.value)">
            <span class="font-weight-light font-size-12">{{
              preset.label
            }}</span>
            <span class="text-disabled font-size-12 font-weight-light ml-1">{{
              preset.date
            }}</span>
          </v-btn>
        </div>
        <b-input-date
          v-model="task.deadline"
          :error-messages="getErrorMessages(v$.deadline)"
          :loading="$loading.isLoading('updateDeadline')"
          :readonly="disabled || $loading.isLoading('updateDeadline')"
          hide-details
          time-picker
          @blur="v$.deadline.$touch()"></b-input-date>
      </div>
    </div>
  </b-section-loader>
</template>

<script lang="ts">
import { useCreateTask } from '@/pages/task/helpers/createTask'
import { getErrorMessages } from 'best-modules/utils'
import { computed } from 'vue'
import { omit } from 'lodash'
import { BInputDate, BSectionLoader, BTextarea } from 'best-modules/components'
import EmployeeAutocomplete from '@/components/inputs/employee-autocomplete/EmployeeAutocomplete.vue'
import moment from 'moment'

export default {
  name: 'CreateTask',
  components: {
    BInputDate,
    BSectionLoader,
    BTextarea,
    EmployeeAutocomplete,
  },
  props: {
    dialog: {
      type: Object,
      required: true,
      default: null,
    },
  },
  methods: { getErrorMessages },
  setup(props) {
    const taskData = props.dialog.dialogData?.taskData
    const createTaskState = useCreateTask(taskData)

    const { task } = createTaskState

    Object.assign(task, taskData || {})

    const getDeadlineDate = (days: number) => moment().add(days, 'days')
    const getDeadlinePreset = (days: number, label: string) => {
      const date = getDeadlineDate(days)

      return {
        label,
        date: date.format('DD.MM'),
        value: date.format('DD.MM.YYYY 00:00'),
      }
    }

    const deadlinePresets = computed(() => [
      getDeadlinePreset(0, 'Сьогодні'),
      getDeadlinePreset(1, 'Завтра'),
      getDeadlinePreset(3, '+3 дні'),
      getDeadlinePreset(7, 'Тиждень'),
      getDeadlinePreset(14, '+14 днів'),
    ])

    const topicLength = computed(() => task.topic?.length || 0)
    const descriptionLength = computed(() => task.description?.length || 0)
    const setDeadline = (value: string) => {
      task.deadline = value
      createTaskState.v$.value.deadline.$touch()
    }

    return {
      task,
      deadlinePresets,
      topicLength,
      descriptionLength,
      setDeadline,
      ...omit(createTaskState, 'task'),
    }
  },
}
</script>
